import {action, makeObservable, observable} from 'mobx'

/**
 * Bidirectional map, providing bidirectional mapping from key to value but also value to key.
 * This means that both forward and backwards mapping has to be unique, so it is not possible to have multiple keys with same value (which would be ok with basic Map).
 *
 * Invariant: `BidirectionalMap.get(a) === b` if and only if `BidirectionalMap.inv.get(b) === a`
 */
export class BidirectionalMap<TKey, TValue> implements Map<TKey, TValue> {
    private forwardMap: Map<TKey, TValue>
    private backwardMap: Map<TValue, TKey>

    private _inv?: BidirectionalMap<TValue, TKey>

    public constructor(iterable?: Iterable<readonly [TKey, TValue]> | null | undefined) {
        // Can't be done via decorators because of the inverse map have to share underlaying map
        this.forwardMap = observable.map(new Map<TKey, TValue>())
        this.backwardMap = observable.map(new Map<TValue, TKey>())

        if (iterable) {
            for (const [key, value] of iterable) {
                this.set(key, value)
            }
        }

        this.get = this.get.bind(this)
        this.has = this.has.bind(this)
        this.forEach = this.forEach.bind(this)
        this.keys = this.keys.bind(this)
        this.values = this.values.bind(this)
        this.entries = this.entries.bind(this)
        this[Symbol.iterator] = this[Symbol.iterator].bind(this)

        makeObservable(this)
    }

    /**
     * Inverse of this map. Uses same underlaying storage, so changes to `this` affect `inv` as well.
     */
    public get inv(): BidirectionalMap<TValue, TKey> {
        if (this._inv === undefined) {
            this._inv = new BidirectionalMap()
            this._inv.forwardMap = this.backwardMap
            this._inv.backwardMap = this.forwardMap
        }
        return this._inv
    }

    public get size() {
        return this.forwardMap.size
    }

    /**
     * Sets key-value mapping.
     * If either `key` or `value` is already in the map an error will be thrown.
     */
    public set(key: TKey, value: TValue): this
    /**
     * Sets key-value mapping.
     * If either `key` or `value` is already in the map an error will be thrown unless `overrride` is also specified. In which case previous key/value will be removed first.
     * @param override Remove old mapping instead of throwing error?
     */
    public set(key: TKey, value: TValue, override: boolean): this
    @action.bound
    public set(key: TKey, value: TValue, override?: boolean): this {
        if (this.has(key) && this.get(key) !== value) {
            if (!override) {
                throw new Error('Key already exists in the bidirectional map')
            }
            this.delete(key)
        }
        if (this.inv.has(value) && this.inv.get(value) !== key) {
            if (!override) {
                throw new Error('Value already exists in the bidirectional map')
            }
            this.inv.delete(value)
        }

        this.forwardMap.set(key, value)
        this.backwardMap.set(value, key)
        return this
    }

    public get(key: TKey): TValue | undefined {
        return this.forwardMap.get(key)
    }

    public has(key: TKey): boolean {
        return this.forwardMap.has(key)
    }

    @action.bound
    public delete(key: TKey): boolean {
        if (this.has(key)) {
            const value = this.get(key) as TValue
            this.forwardMap.delete(key) && this.backwardMap.delete(value)
            return true
        }
        return false
    }

    @action.bound
    public clear(): void {
        this.forwardMap.clear()
        this.backwardMap.clear()
    }

    public forEach(callbackfn: (value: TValue, key: TKey, map: Map<TKey, TValue>) => void, thisArg?: any): void {
        this.forwardMap.forEach(callbackfn, thisArg)
    }

    public entries(): IterableIterator<[TKey, TValue]> {
        return this.forwardMap.entries()
    }

    public keys(): IterableIterator<TKey> {
        return this.forwardMap.keys()
    }

    public values(): IterableIterator<TValue> {
        return this.forwardMap.values()
    }

    public [Symbol.iterator](): IterableIterator<[TKey, TValue]> {
        return this.forwardMap[Symbol.iterator]()
    }

    public [Symbol.toStringTag] = 'BidirectionalMap'
}
